import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/content";

export const adminApi = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
adminApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url.includes("/auth/")) {
      original._retry = true;
      refreshing = refreshing || adminApi.post("/auth/refresh").finally(() => { refreshing = null; });
      try {
        await refreshing;
        return adminApi(original);
      } catch {
        window.dispatchEvent(new Event("admin-logged-out"));
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = signed out

  const check = useCallback(async () => {
    try {
      setUser((await adminApi.get("/auth/me")).data);
    } catch {
      try {
        setUser((await adminApi.post("/auth/refresh")).data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    check();
    const out = () => setUser(false);
    window.addEventListener("admin-logged-out", out);
    return () => window.removeEventListener("admin-logged-out", out);
  }, [check]);

  const login = async (email, password) => setUser((await adminApi.post("/auth/login", { email, password })).data);
  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="site min-h-screen bg-sage grid place-items-center text-moss" data-testid="admin-loading">Loading...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
};
